import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, LineChart, DollarSign, Clock } from 'lucide-react';
import LoadingAnimation from '../../../../components/anims/LoadingAnimation';
import FormattedDate from '../../../../utils/DateFormat';
import formatCurrency from '../../../../utils/formatCurrency';

// --- Componente principal para mostrar las estadísticas ---

const VistaEstadisticasProducto = ({ id_producto, token }) => {
    // --- Estados del componente ---
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Efecto para llamar a la API cuando cambie el ID del producto ---
    useEffect(() => {
        const fetchEstadisticas = async () => {
            if (!id_producto) return;

            setLoading(true);
            setError(null);
            try {
                // Simulación de llamada a la API
                const response = await axios.get(`/api/productos/estadisticas/${id_producto}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setStats(response.data.data);
            } catch (err) {
                console.error('Error al cargar las estadísticas:', err);
                setError('No se pudieron cargar las estadísticas del producto.');
            } finally {
                setLoading(false);
            }
        };

        fetchEstadisticas();
    }, [id_producto, token]);

    // --- Lógica de renderizado condicional ---
    if (loading) {
        return <LoadingAnimation />;
    }

    if (error) {
        return <div className="text-center text-red-500 font-semibold p-4 bg-red-50 rounded-lg">{error}</div>;
    }

    if (!stats) {
        return (
            <div className="text-center text-gray-500 font-semibold text-lg p-4">
                No hay datos estadísticos disponibles para este producto.
            </div>
        );
    }

    // --- Lógica para mostrar los años dinámicamente ---
    const currentYear = new Date().getFullYear();
    const years = {
        anioActual: currentYear,
        anioAnterior: currentYear - 1,
        anioAntepasado: currentYear - 2,
    };

    // --- Función para determinar el color de la sugerencia de inventario ---
    const getSugerenciaColor = (sugerencia) => {
        if (!sugerencia) return { bgColor: 'bg-gray-100', borderColor: 'border-gray-400', textColor: 'text-gray-800' };
        if (sugerencia.includes('URGENTE')) return { bgColor: 'bg-red-50', borderColor: 'border-red-500', textColor: 'text-red-800' };
        if (sugerencia.includes('REQUERIDA')) return { bgColor: 'bg-yellow-500', borderColor: 'border-yellow-500', textColor: 'text-yellow-800' };
        if (sugerencia.includes('SALUDABLE')) return { bgColor: 'bg-green-500', borderColor: 'border-green-500', textColor: 'text-green-800' };
        if (sugerencia.includes('EXCESO') || sugerencia.includes('No comprar')) return { bgColor: 'bg-blue-500', borderColor: 'border-blue-500', textColor: 'text-blue-800' };
        if (sugerencia.includes('DEMANDA')) return { bgColor: 'bg-orange-100', borderColor: 'border-orange-500', textColor: 'text-orange-500' };
        return { bgColor: 'bg-gray-100', borderColor: 'border-gray-400', textColor: 'text-gray-800' };
    };

    const sugerenciaColors = getSugerenciaColor(stats.sugerenciaInventario);

    // --- Renderizado del componente ---
    return (
        <div className="p-4 bg-gray-50 rounded-xl min-h-screen font-sans antialiased">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Indicadores Clave de Producto</h2>
            
            {/* Tarjeta de Sugerencia Principal */}
            <div className={`flex items-center justify-center text-center flex-col p-4 rounded-lg border-l-4 mb-6 transition-colors duration-200 ${sugerenciaColors.bgColor} ${sugerenciaColors.borderColor}`}>
                <Package className={`w-10 h-10 mb-2 shrink-0 ${sugerenciaColors.textColor}`} />
                <p className={`font-bold text-lg ${sugerenciaColors.textColor}`}>Recomendación de Compra</p>
                <p className={`${sugerenciaColors.textColor} text-xl font-semibold`}>{stats.sugerenciaInventario}</p>
            </div>
            
            <hr className="my-6 border-t border-gray-200" />
            
            {/* Sección de Ventas Anuales */}
                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-gray-700 mb-4 text-center">Ventas Anuales (Unidades)</h3>
                            <div className="grid grid-cols-3 gap-2 md:gap-4">
                                <div className="flex flex-col items-center p-3 bg-yellow-500 rounded-lg shadow-sm border border-gray-100">
                                    <p className="text-sm font-semibold text-gray-500">{years.anioActual}</p>
                                    <p className="text-2xl font-bold text-gray-800">{stats.ventasAnuales.anioActual}</p>
                                </div>
                                <div className="flex flex-col items-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                    <p className="text-sm font-semibold text-gray-500">{years.anioAnterior}</p>
                                    <p className="text-2xl font-bold text-gray-800">{stats.ventasAnuales.anioAnterior}</p>
                                </div>
                                <div className="flex flex-col items-center p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                                    <p className="text-sm font-semibold text-gray-500">{years.anioAntepasado}</p>
                                    <p className="text-2xl font-bold text-gray-800">{stats.ventasAnuales.anioAntepasado}</p>
                                </div>
                            </div>
                        </div>

                        <hr className="my-6 border-t border-gray-200" />

                        {/* Sección de Métricas Clave */}
            <div className="space-y-6">
                {/* Inventario y Demanda */}
                <div>
                    <div className="flex items-center mb-2">
                        <Package className="w-5 h-5 text-emerald-500 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-700">Inventario y Demanda</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Stock Actual:</p>
                            <p className="font-bold text-lg text-emerald-600">{stats.stockActual} unidades</p>
                        </div>
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Ventas (últimos 90 días):</p>
                            <p className="font-semibold">{stats.ventasUltimos90Dias} unidades</p>
                        </div>
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Días de Inventario Restantes:</p>
                            <p className="font-semibold">{stats.diasDeInventarioRestantes}</p>
                        </div>
                    </div>
                </div>

                {/* Comportamiento de Venta */}
                <div>
                    <div className="flex items-center mb-2">
                        <LineChart className="w-5 h-5 text-indigo-500 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-700">Comportamiento de Venta</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Última Venta:</p>
                            <p className="font-semibold text-right">{FormattedDate(stats.ultimaVenta.fecha)}</p>
                        </div>
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Frecuencia (Año Actual):</p>
                            <p className="font-semibold">{stats.frecuenciaVentaAnioActual}</p>
                        </div>
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Promedio Unidades por Venta:</p>
                            <p className="font-semibold">{stats.promedioUnidadesPorVenta}</p>
                        </div>
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Indicador de Rotación:</p>
                            <p className="font-bold text-purple-600">{stats.indicadorRotacion}</p>
                        </div>
                    </div>
                </div>

                {/* Historial de Compras */}
                <div>
                    <div className="flex items-center mb-2">
                        <DollarSign className="w-5 h-5 text-orange-500 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-700">Historial de Compras</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Última Compra:</p>
                            <p className="font-semibold text-right">{FormattedDate(stats.ultimaCompra.fecha)}</p>
                        </div>
                        <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Costo Última Compra:</p>
                            <p className="font-semibold text-orange-600">{isNaN(stats.ultimaCompra.costo) ? 'N/A' : formatCurrency(stats.ultimaCompra.costo)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VistaEstadisticasProducto;
